import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/auth/store';
import { useApiKeyDetail, useUpdateApiKey } from '../api';
import { apiKeyFormSchema, type ApiKeyFormInput } from '../schema';
import { ApiKeyForm } from './ApiKeyForm';

export function UpdateApiKeyPage() {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.userId);
  const { id: idParam } = useParams<{ id: string }>();
  const apiKeyId = Number(idParam);

  const detail = useApiKeyDetail(apiKeyId);
  const update = useUpdateApiKey();

  const form = useForm<ApiKeyFormInput>({
    resolver: zodResolver(apiKeyFormSchema),
    mode: 'onTouched',
    defaultValues: { secretKey: '', ip: '', apikey: '' },
  });

  useEffect(() => {
    if (!detail.data) return;
    form.reset({
      secretKey: detail.data.secretKey ?? '',
      ip: detail.data.ip ?? '',
      apikey: detail.data.apikey ?? '',
    });
  }, [detail.data, form]);

  const onSubmit = (input: ApiKeyFormInput) => {
    update.mutate(
      {
        id: apiKeyId,
        ip: input.ip.trim(),
        apikey: input.apikey,
        userId: Number(userId),
        secretKey: input.secretKey,
      },
      {
        onSuccess: (res) => {
          if (res?.message === 'Parameters Incorrect') {
            toast.error(res.message);
            return;
          }
          toast.success(res?.message ?? 'API key updated');
          navigate('/user-api-key-list');
        },
        onError: () => toast.error('Could not update the API key.'),
      },
    );
  };

  return (
    <ApiKeyForm mode="update" form={form} onSubmit={onSubmit} isPending={update.isPending} />
  );
}
